<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import CheckEmailOverlay from './overlays/CheckEmail.vue'
import LoginOverlay from './overlays/Login.vue'
import type { LoginProps } from './types'
import PasswordResetOverlay from './overlays/PasswordReset.vue'
import ResetOverlay from './overlays/Reset.vue'
import SelectAccountOverlay from './overlays/SelectAccount.vue'
import TwoFactorOverlay from './overlays/2fa.vue'
import { useI18n } from '@/composables/i18n'

const { i18n } = useI18n()

defineOptions({
    compatConfig: {
        MODE: 3,
    },
})

const props = withDefaults(defineProps<LoginProps>(), {
    errors: () => [],
    passwordForgottenUrl: '',
    loginUrl: '',
    accounts: () => [],
    twoFactor: null,
})

const showTwoFactor = computed(() => props.twoFactor !== null)
const showAccountSelection = computed(() => props.accounts.length > 0)
const showPasswordReset = computed(() => props.passwordReset !== null)
const showReset = computed(() => props.reset !== null)
const showCheckEmail = computed(() => props.checkEmail !== null)

const showOverlay = ref(false)

onMounted(() => {
    // make sure the overlay is shown after the initial render to allow for transition animations
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            showOverlay.value = true
        })
    })
})
</script>

<template>
    <div class="auth-page">
        <a
            href="https://www.uneto.de"
            class="logo"
            rel="noopener noreferrer"
        >
            <img
                src="@img/uneto/logo-black.svg"
                alt="uneto"
            />
        </a>

        <main
            class="auth-overlay"
            :class="{'show': showOverlay}"
        >
            <TwoFactorOverlay
                v-if="showTwoFactor && props.twoFactor"
                :two-factor="props.twoFactor"
            />
            <SelectAccountOverlay
                v-else-if="showAccountSelection"
                :accounts="props.accounts"
            />
            <PasswordResetOverlay
                v-else-if="showPasswordReset && props.passwordReset"
                :password-reset="props.passwordReset"
            />
            <ResetOverlay
                v-else-if="showReset && props.reset"
                :reset="props.reset"
            />
            <CheckEmailOverlay
                v-else-if="showCheckEmail && props.checkEmail"
                :check-email="props.checkEmail"
            />
            <LoginOverlay
                v-else
                :errors="props.errors"
                :login-url="props.loginUrl"
                :password-forgotten-url="props.passwordForgottenUrl"
            />
        </main>

        <footer class="page-footer">
            <div class="copyright">
                © {{ new Date().getFullYear() }} Speicherhafen GmbH
            </div>

            <div class="legal">
                <a
                    href="https://www.speicherhafen.de/index.php/impressum/"
                    target="_blank"
                    rel="noopener noreferrer"
                >{{ i18n.t('Impressum') }}</a>
                <a
                    href="https://www.speicherhafen.de/index.php/datenschutz/"
                    target="_blank"
                    rel="noopener noreferrer"
                >{{ i18n.t('Datenschutz') }}</a>
            </div>
        </footer>
    </div>
</template>

<style scoped>
@reference "@/assets/styles/config.css";

.auth-page {
    @apply flex flex-col items-center justify-center min-h-screen bg-gray-100;
    background: #DDE5E9 url("@img/uneto/bg/login.jpg") center center no-repeat;
    background-size: cover;
}

.logo {
    @apply absolute block top-9 left-9;

    img {
        @apply block h-auto;
        width:96px;
    }

    @media (min-width: theme(--breakpoint-md)) {
        img {
            width: 128px;
        }
    }
}

.auth-overlay {
    --overlay-x: 50vw;
    --overlay-y: calc(50vh + var(--spacing) * 4);
    --overlay-px: calc(var(--spacing) * 4);
    --overlay-py: calc(var(--spacing) * 4);
    @apply absolute z-100 bg-white rounded-tr-xl rounded-bl-xl border shadow-md;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-color:#dde6ec;
    left: var(--overlay-x);
    top: var(--overlay-y);
    min-width:22rem;
    max-width: 23rem;
    max-height: 80vh;
    padding: var(--overlay-py) var(--overlay-px);
    opacity: 0;
    transform: translate(-50%, calc(-50% + 2rem));
    transition: all 0.6s var(--ease-out-cubic);

    &.show {
        opacity: 1;
        transform: translate(-50%, -50%);
    }

    @media (min-width: theme(--breakpoint-sm)) {
        --overlay-x: 15vw;
        @apply max-w-sm;
        transform: translate(0%, calc(-50% + 2rem));

        &.show {
            transform: translate(0%, -50%);
        }
    }


    @media (min-width: theme(--breakpoint-md)) {
        --overlay-px: calc(var(--spacing) * 8);
        --overlay-py: calc(var(--spacing) * 8);
        @apply max-w-md p-8;
    }
}

.page-footer {
    @apply absolute bottom-0 left-0 w-full px-9 pb-6 flex flex-col-reverse gap-2 items-center justify-between text-gray-500 text-sm font-semibold;
    color:#496F82;

    @media (min-width: theme(--breakpoint-sm)) {
        @apply flex-row;
    }

    .legal {
        @apply flex gap-5;

        a {
            @apply hover:underline;
        }
    }
}
</style>
